/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  OSKTranslationKeyWithParams,
  OSKTranslationPairs,
  OSKTranslations
} from '../types/translation.type';
import { Observable, merge } from 'rxjs';
import { OSKLocaleService } from '@oskey/locale';

/**
 * Service that will handle custom translation logic and return translated texts based on keys.
 *
 * It is a wrapper around the NgxTranslate service ({@link TranslateService}) so custom logic can be added.
 */
@Injectable({
  providedIn: 'root'
})
export class OSKTranslateService {
  private readonly localeService: OSKLocaleService = inject(OSKLocaleService);
  private readonly ngxTranslate: TranslateService = inject(TranslateService);
  private readonly translationRevision = signal<number>(0);

  constructor() {
    merge(
      this.ngxTranslate.onLangChange,
      this.ngxTranslate.onTranslationChange,
      this.ngxTranslate.onDefaultLangChange
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.translationRevision.update((v) => v + 1);
      });

    const initialLang = this.localeService.locale() || this.ngxTranslate.defaultLang;
    if (initialLang && !this.ngxTranslate.currentLang) {
      this.ngxTranslate.use(initialLang);
    }
  }

  instant(key: string, params?: object): string {
    this.localeService.locale();
    this.translationRevision();
    return this.ngxTranslate.instant(key, params);
  }

  private resolveTranslatedOrEmpty(key: string, params?: Record<string, unknown>): string {
    const translated = this.ngxTranslate.instant(key, params);
    return typeof translated === 'string' && translated !== key ? translated : '';
  }

  stream(key: string, params?: object): Observable<string> {
    // TODO: add logic for custom translations
    return this.ngxTranslate.stream(key, params);
  }

  /**
   * Receives an object containing the field names and their corresponding translation keys.
   *
   * Translates the keys and returns the values as values for the corresponding object fields.
   *
   * The object fields that are passsed are then used to access the translated message.
   *
   * The returned signal is computed from the selected locale, which means that if the locale is changed, the component will receive the new translations immediatelly.
   *
   * @usageNotes
   * ```
   * // In the component
   * private readonly translateService: OSKTranslateService = inject(OSKTranslateService);
   *
   * protected texts: Signal<OSKTranslations> = this.translateService.getTranslations({
   *  name: 'translations.name',
   *  age: 'translations.age',
   * });
   *
   * // To use them
   * texts().name;
   * texts().age;
   * ```
   *
   * @param { OSKTranslationPairs } translationPairs {@link OSKTranslationPairs} object containing the keys that we want to translate.
   * @returns { Signal } A {@link Signal} of an object containing all the translations.
   */
  getTranslations(translationPairs: OSKTranslationPairs): Signal<OSKTranslations> {
    return computed(() => {
      this.localeService.locale();
      this.translationRevision();
      const translations: OSKTranslations = {};

      for (const key in translationPairs) {
        if (typeof translationPairs[key] === 'string') {
          translations[key] = this.resolveTranslatedOrEmpty(translationPairs[key] as string);
        } else {
          translations[key] = this.resolveTranslatedOrEmpty(
            (<OSKTranslationKeyWithParams>translationPairs[key]).key,
            (<OSKTranslationKeyWithParams>translationPairs[key]).params as Record<string, unknown>
          );
        }
      }

      return translations;
    });
  }

  /**
   * Used for translating single values.
   *
   * Receives a translation key.
   *
   * Translates the key and returns the translated value.
   *
   * The returned signal is computed from the selected locale, which means that if the locale is changed, the component will receive the new translation immediatelly.
   *
   * @usageNotes
   * ```
   * // In the component
   * private readonly translateService: OSKTranslateService = inject(OSKTranslateService);
   *
   * protected name: Signal<string> = this.translateService.getTranslations('translations.name');
   *
   * // To use it
   * name();
   * ```
   *
   * @param { string } key {@link OSKTranslationPairs} Translation key
   * @returns { Signal } A {@link Signal} of string containing the translated value.
   */
  getTranslation(key: string, params?: Record<string, unknown>): Signal<string> {
    return computed(() => {
      this.localeService.locale();
      this.translationRevision();
      return this.resolveTranslatedOrEmpty(key, params);
    });
  }
}
